import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import { SpinnerGap, Plus, Trash, PencilSimple, FloppyDisk, UserCirclePlus, Trophy } from '@phosphor-icons/react';
import QuarterlyBonusSummary from './QuarterlyBonusSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatINR = (a) => `₹${(a || 0).toLocaleString('en-IN')}`;

const ROLE_OPTIONS = [
  { value: 'sales_recruiter', label: 'Sales Recruiter' },
  { value: 'sales_lead', label: 'Sales Lead' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'technical_recruiter', label: 'Technical Recruiter' },
  { value: 'recruiting_lead', label: 'Recruiting Lead' },
  { value: 'recruiting_manager', label: 'Recruiting Manager' },
  { value: 'sourcing_recruiter', label: 'Sourcing Recruiter' },
  { value: 'sourcing_lead', label: 'Sourcing Lead' },
  { value: 'sourcing_manager', label: 'Sourcing Manager' },
];

const BonusRulesPage = () => {
  const [tiers, setTiers] = useState([]);
  const [bonusRules, setBonusRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tiers');

  // Tier modal state
  const [showTierModal, setShowTierModal] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [tierForm, setTierForm] = useState({ name: '', module: 'sales', roles: [] });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tiersRes, rulesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/bonus-tiers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/bonus-rules`, { withCredentials: true }),
      ]);
      setTiers(tiersRes.data);
      setBonusRules(rulesRes.data);
    } catch (e) {
      console.error('Failed to fetch:', e);
    } finally { setLoading(false); }
  };

  // Tier CRUD
  const openTierModal = (tier = null) => {
    if (tier) {
      setEditTier(tier);
      setTierForm({ name: tier.name, module: tier.module, roles: [...tier.roles] });
    } else {
      setEditTier(null);
      setTierForm({ name: '', module: 'sales', roles: [] });
    }
    setShowTierModal(true);
  };

  const addRoleToTier = () => {
    setTierForm(f => ({ ...f, roles: [...f.roles, { role: '', label: '', amount: 0 }] }));
  };

  const updateRoleInTier = (idx, field, value) => {
    setTierForm(f => {
      const roles = [...f.roles];
      roles[idx] = { ...roles[idx], [field]: value };
      if (field === 'role') {
        const opt = ROLE_OPTIONS.find(r => r.value === value);
        if (opt) roles[idx].label = opt.label;
      }
      return { ...f, roles };
    });
  };

  const removeRoleFromTier = (idx) => {
    setTierForm(f => ({ ...f, roles: f.roles.filter((_, i) => i !== idx) }));
  };

  const saveTier = async () => {
    if (!tierForm.name.trim()) return alert('Tier name is required');
    setSaving(true);
    try {
      if (editTier) {
        await axios.put(`${API_URL}/api/admin/bonus-tiers/${editTier.id}`, tierForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/bonus-tiers`, tierForm, { withCredentials: true });
      }
      setShowTierModal(false);
      fetchAll();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this bonus tier?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/bonus-tiers/${id}`, { withCredentials: true });
      fetchAll();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Bonus Rules (Multiplier Tiers)
  const handleAddRule = () => {
    setBonusRules([...bonusRules, { id: `new-${Date.now()}`, target_percent_min: 0, target_percent_max: 100, multiplier: 1.0, description: 'New rule' }]);
  };
  const handleRemoveRule = (i) => setBonusRules(bonusRules.filter((_, idx) => idx !== i));
  const handleUpdateRule = (i, field, val) => {
    const u = [...bonusRules]; u[i] = { ...u[i], [field]: val }; setBonusRules(u);
  };
  const handleSaveRules = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/bonus-rules`, bonusRules, { withCredentials: true });
      alert('Bonus rules saved!');
      fetchAll();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const salesTiers = tiers.filter(t => t.module === 'sales');
  const recruitingTiers = tiers.filter(t => t.module === 'recruiting');

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  const renderTierCard = (tier) => (
    <Card key={tier.id} className="border-slate-200" data-testid={`tier-card-${tier.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-['Outfit']">{tier.name}</CardTitle>
            <Badge className={tier.module === 'sales' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-teal-100 text-teal-700 text-[10px]'}>
              {tier.module}
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => openTierModal(tier)} data-testid={`edit-tier-${tier.id}`}>
              <PencilSimple size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" onClick={() => deleteTier(tier.id)} data-testid={`delete-tier-${tier.id}`}>
              <Trash size={16} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {tier.roles.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">No roles configured</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2 px-3 text-slate-500 font-semibold text-xs uppercase">S.No</th>
                <th className="text-left py-2 px-3 text-slate-500 font-semibold text-xs uppercase">Role</th>
                <th className="text-right py-2 px-3 text-slate-500 font-semibold text-xs uppercase">Amount</th>
              </tr>
            </thead>
            <tbody>
              {tier.roles.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-slate-500">{i + 1}</td>
                  <td className="py-2 px-3 font-medium text-slate-800">{r.label || r.role?.replace(/_/g, ' ')}</td>
                  <td className="py-2 px-3 text-right font-bold text-emerald-700">{formatINR(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="bonus-rules-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
            Bonus Configuration
          </h1>
          <p className="text-slate-500 mt-1">Configure role-based bonus amounts and quarterly multiplier rules</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openTierModal()} data-testid="add-tier-btn">
          <Plus size={20} className="mr-2" />
          Add Bonus Tier
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="tiers" className="data-[state=active]:bg-white">Role-Based Amounts</TabsTrigger>
          <TabsTrigger value="multipliers" className="data-[state=active]:bg-white">Multiplier Rules</TabsTrigger>
          <TabsTrigger value="quarterly" className="data-[state=active]:bg-white flex items-center gap-1"><Trophy size={14} /> Quarterly Bonus</TabsTrigger>
        </TabsList>

        <TabsContent value="tiers" className="space-y-6 mt-4">
          {/* Sales Bonus Tiers */}
          {salesTiers.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">SALES</Badge> Sales Bonus Tiers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {salesTiers.map(renderTierCard)}
              </div>
            </div>
          )}

          {/* Recruiting Bonus Tiers */}
          {recruitingTiers.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Badge className="bg-teal-100 text-teal-700">RECRUITING</Badge> Recruiting Bonus Tiers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recruitingTiers.map(renderTierCard)}
              </div>
            </div>
          )}

          {tiers.length === 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center text-slate-500">
                <p>No bonus tiers configured yet.</p>
                <Button variant="outline" className="mt-4" onClick={() => openTierModal()}>Add Your First Tier</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="multipliers" className="space-y-4 mt-4">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700">
                <strong>Calculation:</strong> Bonus = Base Amount (from tier) x Multiplier (based on target achievement %)
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-['Outfit']">Quarterly Multiplier Tiers</CardTitle>
                  <CardDescription>Define multipliers for different target achievement ranges</CardDescription>
                </div>
                <Button variant="outline" onClick={handleAddRule} data-testid="add-rule-btn">
                  <Plus size={16} className="mr-1" /> Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {bonusRules.length === 0 ? (
                <p className="text-center py-6 text-slate-500">No multiplier rules configured</p>
              ) : (
                bonusRules.sort((a, b) => a.target_percent_min - b.target_percent_min).map((rule, i) => (
                  <div key={rule.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center gap-4">
                    <div className="grid grid-cols-4 gap-3 flex-1">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Target Min (%)</Label>
                        <Input type="number" value={rule.target_percent_min} onChange={(e) => handleUpdateRule(i, 'target_percent_min', parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Target Max (%)</Label>
                        <Input type="number" value={rule.target_percent_max} onChange={(e) => handleUpdateRule(i, 'target_percent_max', parseFloat(e.target.value) || 100)} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Multiplier</Label>
                        <Input type="number" step="0.1" value={rule.multiplier} onChange={(e) => handleUpdateRule(i, 'multiplier', parseFloat(e.target.value) || 1)} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Description</Label>
                        <Input value={rule.description} onChange={(e) => handleUpdateRule(i, 'description', e.target.value)} placeholder="e.g., Below target" className="h-8 text-sm" />
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 flex-shrink-0" onClick={() => handleRemoveRule(i)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                ))
              )}
              <Button className="w-full bg-blue-600 hover:bg-blue-700 mt-4" onClick={handleSaveRules} disabled={saving} data-testid="save-rules-btn">
                {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
                Save Multiplier Rules
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quarterly" className="mt-4">
          <QuarterlyBonusSummary />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editTier ? 'Edit Bonus Tier' : 'Add New Bonus Tier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tier Name *</Label>
                <Input placeholder="e.g., High End, Low End" value={tierForm.name} onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })} data-testid="tier-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Module</Label>
                <Select value={tierForm.module} onValueChange={(v) => setTierForm({ ...tierForm, module: v })}>
                  <SelectTrigger data-testid="tier-module-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="recruiting">Recruiting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Roles */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Role Amounts</Label>
                <Button type="button" variant="outline" size="sm" onClick={addRoleToTier} data-testid="add-role-btn">
                  <UserCirclePlus size={14} className="mr-1" /> Add Role
                </Button>
              </div>
              {tierForm.roles.length === 0 ? (
                <p className="text-sm text-slate-400 py-3 text-center">No roles added yet. Click "Add Role" above.</p>
              ) : (
                <div className="space-y-2">
                  {tierForm.roles.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                      <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                      <Select value={r.role} onValueChange={(v) => updateRoleInTier(i, 'role', v)}>
                        <SelectTrigger className="flex-1 h-8 text-sm" data-testid={`role-select-${i}`}><SelectValue placeholder="Select role" /></SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">₹</span>
                        <Input type="number" value={r.amount} onChange={(e) => updateRoleInTier(i, 'amount', parseFloat(e.target.value) || 0)} className="w-24 h-8 text-sm" data-testid={`role-amount-${i}`} />
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 h-8 w-8" onClick={() => removeRoleFromTier(i)}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowTierModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={saveTier} data-testid="save-tier-btn">
              {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : null}
              {editTier ? 'Update' : 'Create'} Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BonusRulesPage;
